import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Loader2, MailCheck, MailX } from 'lucide-react';

/**
 * Token-based email-preferences page. Landing target for the "Manage
 * email preferences" link in broadcast emails.
 *
 * The token is a 30-day HS256 JWT the server signs; it embeds only the
 * user_id + purpose='bp' so it can't be used for anything else. We hit
 * unauthenticated endpoints under /api/broadcast-preferences/{token}.
 */
export default function EmailPreferencesPage() {
  const { token } = useParams();
  const base = process.env.REACT_APP_BACKEND_URL;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [account, setAccount] = useState(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${base}/api/broadcast-preferences/${token}`);
        if (cancelled) return;
        setAccount(res.data);
        setPaused(!!res.data.broadcast_emails_paused);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || 'This link is no longer valid.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [base, token]);

  const save = async (next) => {
    setSaving(true);
    try {
      await axios.post(`${base}/api/broadcast-preferences/${token}`, { paused: next });
      setPaused(next);
      toast.success(next ? 'Broadcast emails paused' : 'Broadcast emails resumed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update preference');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="email-preferences-page">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-xl font-heading">Email Preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 py-6 justify-center">
              <Loader2 size={18} className="animate-spin" /> Loading your preferences...
            </div>
          ) : error ? (
            <div className="text-sm text-red-600 py-4">
              <p className="font-medium">{error}</p>
              <p className="mt-2 text-slate-600">
                Please log in to KR Agency Hub and manage your preferences from your profile settings.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-600">
                Managing preferences for <strong>{account?.email}</strong>.
              </p>
              <div className="flex items-start justify-between gap-3 rounded-md border border-slate-200 p-4">
                <div className="flex items-start gap-3">
                  {paused ? (
                    <MailX size={22} className="text-slate-400 mt-0.5" />
                  ) : (
                    <MailCheck size={22} className="text-emerald-500 mt-0.5" />
                  )}
                  <div>
                    <div className="font-medium text-slate-900">Event announcement emails</div>
                    <p className="text-xs text-slate-500 max-w-xs">
                      Receive an email each time KR Agency Hub publishes a new event you have access to.
                      Transactional emails (registration confirmations, reminders) are always sent regardless.
                    </p>
                  </div>
                </div>
                <Switch
                  checked={!paused}
                  onCheckedChange={(next) => save(!next)}
                  disabled={saving}
                  data-testid="email-preferences-broadcast-toggle"
                />
              </div>
              <div className="flex justify-end pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => (window.location.href = '/')}
                >
                  Return to portal
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
